/**
 * Validation Step
 * 
 * Validate imported data and display errors/warnings
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import axios from 'axios';

interface ValidationStepProps {
  sessionId: string;
  mappings: Record<string, string>;
  onValidationComplete: (result: any) => void;
  onError: (error: string) => void;
  onBack: () => void;
}

const ValidationStep: React.FC<ValidationStepProps> = ({
  sessionId,
  mappings,
  onValidationComplete,
  onError,
  onBack
}) => {
  const [validating, setValidating] = useState(true);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    performValidation();
  }, []);

  const performValidation = async () => {
    setValidating(true);
    try {
      const response = await axios.post(
        '/api/v1/import/validate',
        {
          session_id: sessionId,
          mappings: mappings,
          check_duplicates: true
        }
      );

      if (response.data.success) {
        setValidationResult(response.data.validation_result);
      } else {
        onError('Validation failed. Please check your mappings.');
      }
    } catch (error: any) {
      console.error('Validation failed:', error);
      onError(error.response?.data?.detail || 'Validation failed. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  const handleContinue = () => {
    if (validationResult) {
      onValidationComplete(validationResult);
    }
  };

  const toggleRowExpansion = (rowNumber: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowNumber)) {
      newExpanded.delete(rowNumber);
    } else {
      newExpanded.add(rowNumber);
    }
    setExpandedRows(newExpanded);
  };

  if (validating) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Validating Your Data
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Checking data quality, formats, and business rules...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!validationResult) {
    return (
      <Alert severity="error">
        Validation failed. Please try again.
      </Alert>
    );
  }

  const { valid, errors, warnings, valid_rows, invalid_rows, total_rows, duplicate_rows } = validationResult;

  // Group errors by row
  const errorsByRow = new Map<number, any[]>();
  errors.forEach((error: any) => {
    if (!errorsByRow.has(error.row_number)) {
      errorsByRow.set(error.row_number, []);
    }
    errorsByRow.get(error.row_number)!.push(error);
  });

  // Group warnings by row
  const warningsByRow = new Map<number, any[]>();
  warnings.forEach((warning: any) => {
    if (!warningsByRow.has(warning.row_number)) {
      warningsByRow.set(warning.row_number, []);
    }
    warningsByRow.get(warning.row_number)!.push(warning);
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Validation Results
      </Typography>

      {/* Summary */}
      <Box display="flex" gap={2} mb={3}>
        <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.lighter' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SuccessIcon color="success" />
            <Box>
              <Typography variant="h4">{valid_rows}</Typography>
              <Typography variant="body2">Valid Rows</Typography>
            </Box>
          </Box>
        </Paper>
        
        <Paper sx={{ p: 2, flex: 1, bgcolor: invalid_rows > 0 ? 'error.lighter' : 'grey.100' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon color={invalid_rows > 0 ? 'error' : 'disabled'} />
            <Box>
              <Typography variant="h4">{invalid_rows}</Typography>
              <Typography variant="body2">Invalid Rows</Typography>
            </Box>
          </Box>
        </Paper>
        
        <Paper sx={{ p: 2, flex: 1, bgcolor: warnings.length > 0 ? 'warning.lighter' : 'grey.100' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color={warnings.length > 0 ? 'warning' : 'disabled'} />
            <Box>
              <Typography variant="h4">{warnings.length}</Typography>
              <Typography variant="body2">Warnings</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Status Alert */}
      {valid ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>All data is valid!</strong> You can proceed with the import.
        </Alert>
      ) : (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{invalid_rows} rows have errors.</strong> You can skip invalid rows or fix them and re-upload.
        </Alert>
      )}

      {/* Tabs for Errors and Warnings */}
      {(errors.length > 0 || warnings.length > 0) && (
        <Box sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon fontSize="small" />
                  Errors ({errors.length})
                </Box>
              }
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon fontSize="small" />
                  Warnings ({warnings.length})
                </Box>
              }
            />
          </Tabs>

          {/* Errors Tab */}
          {activeTab === 0 && errors.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="10%"><strong>Row</strong></TableCell>
                    <TableCell width="20%"><strong>Field</strong></TableCell>
                    <TableCell width="20%"><strong>Value</strong></TableCell>
                    <TableCell width="50%"><strong>Error</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errors.map((error: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip label={error.row_number} size="small" color="error" />
                      </TableCell>
                      <TableCell>{error.field}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {error.value || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{error.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Warnings Tab */}
          {activeTab === 1 && warnings.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="10%"><strong>Row</strong></TableCell>
                    <TableCell width="20%"><strong>Field</strong></TableCell>
                    <TableCell width="20%"><strong>Value</strong></TableCell>
                    <TableCell width="50%"><strong>Warning</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warnings.map((warning: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip label={warning.row_number} size="small" color="warning" />
                      </TableCell>
                      <TableCell>{warning.field}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {warning.value || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{warning.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Help Text */}
      <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          💡 What to do next:
        </Typography>
        <Typography variant="body2" component="div">
          {valid ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>All data passed validation checks</li>
              <li>Click "Continue to Import" to proceed</li>
              {warnings.length > 0 && <li>Warnings won't prevent import but should be reviewed</li>}
            </ul>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Option 1:</strong> Fix errors in your file and re-upload</li>
              <li><strong>Option 2:</strong> Continue and skip invalid rows (they won't be imported)</li>
              <li>Review the errors table above to see what needs fixing</li>
            </ul>
          )}
        </Typography>
      </Box>

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between">
        <Button onClick={onBack}>
          Back to Mapping
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          color={valid ? 'primary' : 'warning'}
        >
          {valid ? 'Continue to Import' : 'Continue (Skip Invalid Rows)'}
        </Button>
      </Box>
    </Box>
  );
};

export default ValidationStep;

